import { useEffect, useRef, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/Panel";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Trash2, Upload, AlertTriangle } from "lucide-react";
import { useBusquedaLive } from "@/lib/busquedaHeader";

const vacio = { termino_mecanico: "", termino_catalogo: "", notas: "" };

export default function Sinonimos() {
  const [sinonimos, setSinonimos] = useState([]);
  const [form, setForm] = useState(vacio);
  const [q] = useBusquedaLive("Buscar en sinónimos…");
  const fileRef = useRef(null);
  const [importOpen, setImportOpen] = useState(false);
  const [modo, setModo] = useState("reemplazar");
  const [archivo, setArchivo] = useState(null);
  const [filasArchivo, setFilasArchivo] = useState(null);
  const [importando, setImportando] = useState(false);

  const cargar = () => api.get("/sinonimos").then((r) => setSinonimos(r.data)).catch(() => {});
  useEffect(() => { cargar(); }, []);

  const agregar = async () => {
    try {
      await api.post("/sinonimos", form);
      toast.success("Sinónimo agregado");
      setForm(vacio);
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  const eliminar = async (s) => {
    if (!window.confirm(`¿Eliminar "${s.termino_mecanico}" → "${s.termino_catalogo}"?`)) return;
    try { await api.delete(`/sinonimos/${s.id}`); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const abrirImport = () => {
    setArchivo(null); setFilasArchivo(null); setModo("reemplazar"); setImportOpen(true);
  };

  const elegirArchivo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setArchivo(file);
    try {
      const texto = await file.text();
      const lineas = texto.split(/\r?\n/).filter((l) => l.trim() !== "");
      setFilasArchivo(Math.max(0, lineas.length - 1)); // menos encabezado
    } catch { setFilasArchivo(null); }
  };

  const importar = async () => {
    if (!archivo) { toast.error("Elegí un archivo CSV"); return; }
    if (modo === "reemplazar") {
      const m = filasArchivo != null ? filasArchivo : "los";
      if (!window.confirm(`Esto eliminará los ${sinonimos.length} sinónimos actuales y cargará los ${m} del archivo. ¿Continuar?`)) return;
    }
    const fd = new FormData(); fd.append("file", archivo); fd.append("modo", modo);
    setImportando(true);
    try {
      const { data } = await api.post("/sinonimos/importar-csv", fd);
      if (data.modo === "reemplazar") {
        toast.success(`Reemplazo completo · Eliminados: ${data.eliminados} · Cargados: ${data.cargados}`);
      } else {
        toast.success(`Importados: ${data.importados} (${data.duplicados} duplicados omitidos)`);
      }
      if (data.errores?.length) toast.warning(`${data.errores.length} filas con errores`);
      setImportOpen(false);
      cargar();
    } catch (err) { toast.error(errorMsg(err)); }
    finally { setImportando(false); }
  };

  const qn = q.trim().toLowerCase();
  const lista = qn ? sinonimos.filter((s) =>
    s.termino_mecanico.toLowerCase().includes(qn) || s.termino_catalogo.toLowerCase().includes(qn)) : sinonimos;

  return (
    <div className="space-y-4" data-testid="sinonimos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Sinónimos de repuestos</h1>
          <p className="text-sm text-muted-foreground">
            Jerga del mecánico → término del catálogo. Un mismo término puede apuntar a varias piezas
            (varias filas). El buscador y las sugerencias de IA los usan automáticamente.
          </p>
        </div>
        <div>
          <Button data-testid="sinonimos-importar-btn" variant="outline" size="sm" onClick={abrirImport}>
            <Upload size={15} className="mr-1" /> Importar CSV
          </Button>
        </div>
      </div>

      <Dialog open={importOpen} onOpenChange={(v) => { if (!importando) setImportOpen(v); }}>
        <DialogContent data-testid="sinonimos-import-dialog" className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload size={18} className="text-indigo-600" /> Importar sinónimos</DialogTitle>
            <p className="text-xs text-muted-foreground">Columnas: termino_mecanico, termino_catalogo, notas (UTF-8).</p>
          </DialogHeader>

          <div className="space-y-3">
            <RadioGroup value={modo} onValueChange={setModo} className="space-y-2">
              <label data-testid="sinonimos-modo-reemplazar" className="flex items-start gap-2 border border-border rounded-sm p-2 cursor-pointer">
                <RadioGroupItem value="reemplazar" className="mt-0.5" />
                <div>
                  <span className="text-sm font-semibold text-pink-700">Reemplazar todo</span>
                  <p className="text-xs text-muted-foreground">Borra los {sinonimos.length} sinónimos actuales y carga solo los del archivo.</p>
                </div>
              </label>
              <label data-testid="sinonimos-modo-agregar" className="flex items-start gap-2 border border-border rounded-sm p-2 cursor-pointer">
                <RadioGroupItem value="agregar" className="mt-0.5" />
                <div>
                  <span className="text-sm font-semibold">Agregar / actualizar</span>
                  <p className="text-xs text-muted-foreground">Suma los nuevos y omite las combinaciones ya existentes.</p>
                </div>
              </label>
            </RadioGroup>

            <div>
              <input type="file" accept=".csv" ref={fileRef} onChange={elegirArchivo} className="hidden" />
              <Button data-testid="sinonimos-elegir-archivo" variant="outline" size="sm" onClick={() => fileRef.current.click()}>
                {archivo ? "Cambiar archivo" : "Elegir archivo CSV"}
              </Button>
              {archivo && (
                <p data-testid="sinonimos-archivo-info" className="text-xs text-muted-foreground mt-1">
                  {archivo.name}{filasArchivo != null ? ` · ${filasArchivo} fila(s)` : ""}
                </p>
              )}
            </div>

            {modo === "reemplazar" && (
              <p className="flex items-start gap-1 text-xs text-pink-700 bg-pink-50 border border-pink-200 rounded-sm px-2 py-1.5">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                Reemplazo total: el archivo pasa a ser la única fuente. Se pedirá confirmación.
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="sinonimos-import-cancelar" onClick={() => setImportOpen(false)} disabled={importando}>Cancelar</Button>
            <Button data-testid="sinonimos-import-confirmar" onClick={importar} disabled={!archivo || importando}
              className={modo === "reemplazar" ? "bg-pink-600 hover:bg-pink-700 text-white" : "bg-indigo-600 hover:bg-indigo-700 text-white"}>
              {importando ? "Importando…" : (modo === "reemplazar" ? "Reemplazar todo" : "Importar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Panel titulo="Agregar sinónimo" testId="sinonimos-agregar-panel">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-44 space-y-1">
            <p className="text-xs font-medium text-slate-600">Término del mecánico</p>
            <Input data-testid="sinonimo-mecanico-input" value={form.termino_mecanico} placeholder="Ej: balatas"
              onChange={(e) => setForm({ ...form, termino_mecanico: e.target.value })} />
          </div>
          <div className="flex-1 min-w-44 space-y-1">
            <p className="text-xs font-medium text-slate-600">Término del catálogo</p>
            <Input data-testid="sinonimo-catalogo-input" value={form.termino_catalogo} placeholder="Ej: pastillas de freno"
              onChange={(e) => setForm({ ...form, termino_catalogo: e.target.value })} />
          </div>
          <div className="flex-1 min-w-44 space-y-1">
            <p className="text-xs font-medium text-slate-600">Notas (opcional)</p>
            <Input data-testid="sinonimo-notas-input" value={form.notas} placeholder="Ej: freno de disco"
              onChange={(e) => setForm({ ...form, notas: e.target.value })} />
          </div>
          <Button data-testid="sinonimo-agregar-btn" onClick={agregar}
            disabled={!form.termino_mecanico.trim() || !form.termino_catalogo.trim()}>
            <Plus size={15} className="mr-1" /> Agregar
          </Button>
        </div>
      </Panel>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary">
              <TableHead>Término del mecánico</TableHead>
              <TableHead>Término del catálogo</TableHead>
              <TableHead className="hidden md:table-cell">Notas</TableHead>
              <TableHead className="w-14"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lista.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8"
                data-testid="sinonimos-vacio">
                No hay sinónimos {qn ? "que coincidan" : "cargados. Agregue uno o importe el CSV inicial."}
              </TableCell></TableRow>
            )}
            {lista.map((s) => (
              <TableRow key={s.id} data-testid={`sinonimo-row-${s.id}`}>
                <TableCell className="font-medium">{s.termino_mecanico}</TableCell>
                <TableCell className="text-slate-700">{s.termino_catalogo}</TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground text-xs">{s.notas}</TableCell>
                <TableCell>
                  <Button data-testid={`sinonimo-eliminar-${s.id}`} variant="ghost" size="icon"
                    className="h-7 w-7 text-destructive" onClick={() => eliminar(s)}>
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
